import { useEffect, useRef, useState } from 'react';
import {
  AtSign,
  Check,
  ChevronRight,
  DollarSign,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { db } from '@/lib/db';
import { EditPostSheet } from '@/components/feed/EditPostSheet';
import { getAchievementDef } from '@/lib/achievements/catalog';
import { mergeReactionCounts } from '@/lib/feed/reaction-counts';
import { gamesWithSettlement, settledLiveBets } from '@/lib/feed/post-settlement';
import { timeAgo } from '@/lib/timeAgo';
import type { Comment, MemberPublic, PostEnriched, ReactionEmoji } from '@/lib/db/types';
import { Avatar } from '@/components/shared/Avatar';
import { PostReactionPicker } from '@/components/feed/PostReactionPicker';
import { StorageImage } from '@/components/shared/StorageImage';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { GroupScoresRecap } from '@/components/feed/GroupScoresRecap';
import { RoundGameRecap } from '@/components/feed/RoundGameRecap';
import { holesLabel, playedPar, scoreToParLabel } from '@/lib/scoring/played-par';

function scoreLabel(score: number, par: number): string {
  return scoreToParLabel(score, par);
}

function renderMentions(text: string, members: Record<string, MemberPublic>): React.ReactNode {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      const handle = part.slice(1);
      const m = Object.values(members).find((x) => x.handle === handle);
      if (m) return <span key={i} className="font-semibold text-gold">@{m.handle}</span>;
    }
    return <span key={i}>{part}</span>;
  });
}

interface Props {
  post: PostEnriched;
  memberMap?: Record<string, MemberPublic>;
  viewerId?: string;
  staggerIndex?: number;
  onOpenProfile?: (id: string) => void;
  onOpenWager?: (post: PostEnriched, opts?: { isNew?: boolean }) => void;
  onOpenGameResult?: (post: PostEnriched, gameIndex: number) => void;
  onOpenMoneyResults?: (post: PostEnriched) => void;
  onPostDeleted?: (postId: string) => void;
  onPostUpdated?: (postId: string, body: string) => void;
}

export function resolvePostMediaUrls(post: Pick<PostEnriched, 'media' | 'photo_url'>): string[] {
  if (post.media && post.media.length > 0) {
    return [...post.media]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((m) => m.storage_url);
  }
  return post.photo_url ? [post.photo_url] : [];
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|webm|ogg)(\?|$)/i.test(url);
}

export function PostCard({
  post,
  memberMap = {},
  viewerId,
  staggerIndex = 0,
  onOpenProfile,
  onOpenWager,
  onOpenGameResult,
  onOpenMoneyResults,
  onPostDeleted,
  onPostUpdated,
}: Props) {
  const [openComments, setOpenComments] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeletePostConfirm, setShowDeletePostConfirm] = useState(false);
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState<string | null>(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bodyText, setBodyText] = useState(post.body || '');
  const [comments, setComments] = useState<Comment[]>([]);
  const [draft, setDraft] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [burstKey, setBurstKey] = useState(0);
  const [commentCount, setCommentCount] = useState(post.comment_count ?? 0);
  const [reactionCount, setReactionCount] = useState(post.reaction_count ?? 0);
  const [likeCount, setLikeCount] = useState(post.like_count ?? 0);
  const [myLiked, setMyLiked] = useState(!!post.my_liked);
  const [myReactions, setMyReactions] = useState<ReactionEmoji[]>(post.my_reactions ?? []);
  const [reactionCounts, setReactionCounts] = useState<Partial<Record<ReactionEmoji, number>>>({});
  const [reactionError, setReactionError] = useState<string | null>(null);
  const lastTapRef = useRef(0);

  useEffect(() => {
    setCommentCount(post.comment_count ?? 0);
    setReactionCount(post.reaction_count ?? 0);
    setLikeCount(post.like_count ?? 0);
    setMyLiked(!!post.my_liked);
    setMyReactions(post.my_reactions ?? []);
    setBodyText(post.body || '');
  }, [post.id, post.comment_count, post.reaction_count, post.like_count, post.my_liked, post.my_reactions, post.body]);

  useEffect(() => {
    if ((post.reaction_count ?? 0) <= 0) {
      return;
    }
    let active = true;
    void db.reactions.list(post.id).then((rows) => {
      if (!active) return;
      setReactionCounts(mergeReactionCounts(rows));
    });
    return () => { active = false; };
  }, [post.id, post.reaction_count]);

  useEffect(() => {
    setReactionCounts({});
  }, [post.id]);

  const isOwner = viewerId === post.author_id;
  const canEditBody = isOwner && post.type !== 'ace' && post.type !== 'achievement';

  const deletePost = async () => {
    setDeleting(true);
    try {
      await db.posts.remove(post.id);
      onPostDeleted?.(post.id);
    } finally {
      setDeleting(false);
      setShowMenu(false);
      setShowDeletePostConfirm(false);
    }
  };

  const isAce = post.type === 'ace';
  const isAchievement = post.type === 'achievement';
  const isRound = post.type === 'round' && post.round;
  /** Completed round posts must never offer feed wagers (even if round join is missing). */
  const isRoundPost = post.type === 'round' || Boolean(post.round_id);
  const isPhoto = post.type === 'status';
  const gross = post.round?.gross_total;
  const holeRange = post.round?.holes ?? 'full';
  const par = post.round?.course
    ? playedPar(holeRange, post.round.course.par, post.round.course_holes)
    : (post.round ? (holeRange === 'full' ? 72 : 36) : null);
  const courseName = post.round?.course?.name || post.metadata?.courseName;
  const teeName = post.round?.tee?.name || post.metadata?.teeName;
  const holesCountLabel = post.round?.holes ? holesLabel(post.round.holes) : null;
  const achDef = post.achievement ? getAchievementDef(post.achievement.achievement_id) : null;
  const games = gamesWithSettlement(post.metadata?.games);
  const allLiveBets = settledLiveBets(post.metadata?.liveBets);
  const guests = post.metadata?.guests || [];
  const tagged = post.tagged_members || [];
  const groupScores = post.metadata?.groupScores || [];
  const recapGames = post.metadata?.recapGames || [];
  const showGroupRecap = isRound && groupScores.length > 0;
  const showGameRecap = isRound && recapGames.length > 0;
  const canViewMoneyResults = Boolean(
    viewerId
    && isRoundPost
    && onOpenMoneyResults
    && (post.metadata?.hasMoneyStakes || games.length > 0 || Boolean(post.metadata?.groupRoundId)),
  );

  const loadComments = async () => {
    if (loadingComments) return;
    setLoadingComments(true);
    try {
      setComments(await db.comments.list(post.id));
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    if (commentCount > 0 && comments.length === 0) {
      void loadComments();
    }
  }, [post.id, commentCount]);

  const toggleComments = () => {
    setOpenComments((v) => !v);
    if (!openComments && comments.length === 0) void loadComments();
  };

  const updateReactionCounts = (emoji: ReactionEmoji, added: boolean) => {
    setReactionCounts((prev) => {
      const next = { ...prev };
      const c = (next[emoji] || 0) + (added ? 1 : -1);
      if (c <= 0) delete next[emoji];
      else next[emoji] = c;
      return next;
    });
  };

  const toggleReaction = async (emoji: ReactionEmoji) => {
    const had = myReactions.includes(emoji);
    setReactionError(null);
    setMyReactions((prev) => (had ? prev.filter((e) => e !== emoji) : [...prev, emoji]));
    setReactionCount((c) => Math.max(0, had ? c - 1 : c + 1));
    updateReactionCounts(emoji, !had);
    try {
      await db.reactions.toggle(post.id, emoji);
    } catch (err) {
      setMyReactions((prev) => (had ? [...prev, emoji] : prev.filter((e) => e !== emoji)));
      setReactionCount((c) => Math.max(0, had ? c + 1 : c - 1));
      updateReactionCounts(emoji, had);
      setReactionError((err as Error).message || 'Could not save reaction');
    }
  };

  const toggleLike = async () => {
    const wasLiked = myLiked;
    setMyLiked(!wasLiked);
    setLikeCount((c) => Math.max(0, wasLiked ? c - 1 : c + 1));
    setBurstKey((k) => k + 1);
    try {
      await db.likes.toggle(post.id);
    } catch {
      setMyLiked(wasLiked);
      setLikeCount((c) => Math.max(0, wasLiked ? c + 1 : c - 1));
    }
  };

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      if (!myLiked) void toggleLike();
      else setBurstKey((k) => k + 1);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      await db.comments.remove(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentCount((c) => Math.max(0, c - 1));
      setPendingDeleteCommentId(null);
    } catch (e) {
      setCommentError((e as Error).message || 'Could not delete comment');
    }
  };

  const addComment = async () => {
    if (!draft.trim() || postingComment) return;
    setPostingComment(true);
    setCommentError(null);
    const text = draft.trim();
    try {
      const created = await db.comments.add(post.id, text);
      setDraft('');
      setComments((prev) => [...prev, created]);
      setCommentCount((c) => c + 1);
    } catch (e) {
      setCommentError((e as Error).message || 'Could not post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const canWager = !isRoundPost && !post.wager && viewerId && post.author_id !== viewerId
    && games.length === 0 && allLiveBets.length === 0
    && recapGames.length === 0 && !post.metadata?.hasMoneyStakes;
  const mediaUrls = resolvePostMediaUrls(post);
  const reactionEntries = Object.entries(reactionCounts)
    .filter(([, count]) => count && count > 0)
    .sort((a, b) => (b[1] || 0) - (a[1] || 0)) as [ReactionEmoji, number][];

  const settledBets = allLiveBets;
  const liveBetNet = settledBets.reduce((acc, b) => {
    const dollars = b.wager_cents / 100;
    if (b.status === 'won') return acc + dollars;
    if (b.status === 'lost') return acc - dollars;
    return acc;
  }, 0);

  const cardShadow = isAce
    ? '0 4px 20px rgba(196,166,98,0.35)'
    : isAchievement
      ? '0 4px 16px rgba(10,30,63,0.25)'
      : undefined;

  if (deleting) return null;

  return (
    <>
    <article
      className={`fade-up rounded-2xl bg-paper ${
        isAce ? 'border-2 border-gold' : isAchievement ? 'border-2 border-navy' : 'border-[1.5px] border-navy'
      } ${cardShadow ? '' : 'shadow-bt'}`}
      style={{ animationDelay: `${staggerIndex * 50}ms`, boxShadow: cardShadow }}
    >
      {isAce && (
        <div className="flex items-center gap-2.5 bg-gradient-to-r from-gold to-gold-deep px-4 py-2.5 text-navy">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy text-lg text-gold">🦅</div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-extrabold tracking-[0.2em]">HOLE IN ONE · +500 POINTS</div>
            <div className="font-serif text-base font-extrabold leading-tight tracking-[-0.01em]">
              {post.author.name} aced hole {post.ace?.hole_num}
            </div>
          </div>
        </div>
      )}
      {isAchievement && (
        <div className="flex items-center gap-2.5 bg-gradient-to-r from-navy to-[#0f2854] px-4 py-3 text-cream">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold text-lg text-navy">
            {achDef?.icon ?? '🏆'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-extrabold tracking-[0.2em] text-gold-bright">ACHIEVEMENT UNLOCKED</div>
            <div className="font-serif text-base font-extrabold leading-tight tracking-[-0.01em]">
              {post.author.name} · {achDef?.name ?? post.achievement?.achievement_id}
            </div>
            {achDef && <div className="mt-0.5 text-[11px] text-white/65">{achDef.desc}</div>}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 px-4 pb-2.5 pt-3.5">
        <button type="button" onClick={() => onOpenProfile?.(post.author.id)} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
          <Avatar member={post.author} size={40} />
          <div className="min-w-0 flex-1">
            <div className="font-serif text-base font-bold leading-tight text-ink">{post.author.name}</div>
            <div className="mt-px text-xs font-medium text-mute">
              @{post.author.handle} · {timeAgo(post.created_at)} ago
            </div>
          </div>
        </button>
        {isOwner && (
          <div className="relative">
            <button type="button" onClick={() => setShowMenu((v) => !v)} className="p-1.5 text-mute" aria-label="Post options">
              <MoreHorizontal size={18} />
            </button>
            {showMenu && (
              <>
                <button type="button" className="bt-flat fixed inset-0 z-10" aria-label="Close menu" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full z-20 mt-1 min-w-[140px] overflow-hidden rounded-xl border border-line bg-paper py-1 shadow-bt-md">
                  {canEditBody && (
                    <button
                      type="button"
                      onClick={() => { setShowMenu(false); setEditing(true); }}
                      className="bt-flat flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-navy hover:bg-cream"
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowDeletePostConfirm(true)}
                    className="bt-flat flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {isRound && gross != null && !showGroupRecap && (
        <div className="relative mx-4 mb-3 overflow-hidden rounded-[14px] bg-navy p-[18px] text-paper">
          <div className="flex items-end gap-4">
            <div>
              <div className="text-[11px] font-medium text-gold-bright">Score</div>
              <div className="mt-0.5 flex items-baseline gap-2.5">
                <span className="font-serif text-[52px] font-bold leading-none tracking-[-0.03em]">{gross}</span>
                {par != null && (
                  <span
                    className="font-mono rounded-lg px-2 py-0.5 text-[13px] font-semibold"
                    style={{
                      color: gross - par <= 0 ? '#dabb7a' : '#ffffff',
                      background: 'rgba(255,255,255,0.12)',
                    }}
                  >
                    {scoreLabel(gross, par)}
                  </span>
                )}
              </div>
            </div>
            {courseName && (
              <div className="min-w-0 flex-1 pb-1">
                <div className="text-[11px] font-medium text-gold-bright">Course</div>
                <div className="mt-0.5 font-serif text-[15px] font-semibold tracking-[-0.01em]">{courseName}</div>
                <div className="mt-0.5 text-xs text-white/55">
                  {teeName || '—'}{par ? ` · Par ${par}` : ''}{holesCountLabel ? ` · ${holesCountLabel}` : ''}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showGroupRecap && (
        <GroupScoresRecap groupScores={groupScores} coursePar={par} />
      )}

      {showGameRecap && (
        <RoundGameRecap games={recapGames} />
      )}

      {canViewMoneyResults && (
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={() => onOpenMoneyResults?.(post)}
            className="flex w-full items-center justify-between rounded-xl border border-gold/40 bg-gold/10 px-3.5 py-3 text-left active:scale-[0.99]"
          >
            <div>
              <div className="text-[10px] font-extrabold tracking-[0.12em] text-gold-deep">PRIVATE SETTLEMENT</div>
              <div className="mt-0.5 text-sm font-bold text-navy">View money results</div>
              <div className="text-[11px] text-ink-soft">Not shown publicly on the feed</div>
            </div>
            <ChevronRight size={18} className="shrink-0 text-navy" />
          </button>
        </div>
      )}

      {bodyText && (isRound || isPhoto || post.type === 'status') && (
        <div className={`px-4 text-[15px] font-medium leading-normal text-ink ${isRound ? 'pb-3' : 'pb-3'}`}>
          {renderMentions(bodyText, memberMap)}
        </div>
      )}

      {(tagged.length > 0 || guests.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 pb-3 text-xs text-ink-soft">
          <AtSign size={12} className="text-gold" />
          <span className="text-mute">with</span>
          {tagged.map((m, i) => (
            <span key={m.id}>
              <button type="button" onClick={() => onOpenProfile?.(m.id)} className="font-semibold text-navy">
                {m.name.split(' ')[0]}
              </button>
              {(i < tagged.length - 1 || guests.length > 0) && ','}
            </span>
          ))}
          {guests.map((g, i) => (
            <span key={`${g.name}-${i}`} className="inline-flex items-center gap-1 font-semibold text-gold-deep">
              {g.name.split(' ')[0]}
              <span className="rounded-sm bg-gold px-1 py-px text-[8px] font-extrabold tracking-wide text-navy">GUEST</span>
              {i < guests.length - 1 && ','}
            </span>
          ))}
        </div>
      )}

      {mediaUrls.length > 0 && (
        <div className="relative w-full cursor-pointer select-none" onClick={handleDoubleTap}>
          {mediaUrls.length === 1 ? (
            isVideoUrl(mediaUrls[0]) ? (
              <video
                src={mediaUrls[0]}
                controls
                playsInline
                className="block max-h-[400px] w-full object-cover"
              />
            ) : (
              <StorageImage src={mediaUrls[0]} alt="" className="block max-h-[400px] w-full object-cover" />
            )
          ) : (
            <div className="no-scrollbar flex overflow-x-auto" style={{ scrollSnapType: 'x mandatory' }}>
              {mediaUrls.map((url, i) => (
                isVideoUrl(url) ? (
                  <video
                    key={`${url}-${i}`}
                    src={url}
                    controls
                    playsInline
                    className="block max-h-[400px] w-full shrink-0 grow-0 basis-full object-cover"
                    style={{ scrollSnapAlign: 'center' }}
                  />
                ) : (
                  <StorageImage
                    key={`${url}-${i}`}
                    src={url}
                    alt=""
                    className="block max-h-[400px] w-full shrink-0 grow-0 basis-full object-cover"
                    style={{ scrollSnapAlign: 'center' }}
                  />
                )
              ))}
            </div>
          )}
          {mediaUrls.length > 1 && (
            <div className="pointer-events-none absolute right-2.5 top-2.5 rounded-full bg-black/60 px-2.5 py-0.5 text-[11px] font-bold text-white">
              {mediaUrls.length} media · swipe
            </div>
          )}
          {burstKey > 0 && (
            <Heart
              key={burstKey}
              size={120}
              fill="#dc2626"
              color="#dc2626"
              strokeWidth={0}
              className="heart-burst"
              style={{ filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.4))' }}
            />
          )}
        </div>
      )}

      

      {allLiveBets.length > 0 && !showGroupRecap && (
        <div className="px-4 pb-3">
          <div className="rounded-[22px] bg-navy p-3.5 text-paper shadow-bt">
            <div className="mb-2.5 flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold text-navy">
                <Sparkles size={16} />
              </div>
              <div className="flex-1 text-[11px] font-bold tracking-wide text-gold-bright">
                SIDE BETS · {settledBets.length}/{allLiveBets.length} SETTLED
              </div>
              <div
                className="font-mono text-base font-bold"
                style={{ color: liveBetNet > 0 ? '#10b981' : liveBetNet < 0 ? '#ef4444' : '#dabb7a' }}
              >
                {liveBetNet >= 0 ? '+' : ''}${liveBetNet.toFixed(0)}
              </div>
            </div>
            {allLiveBets.slice(0, 3).map((b) => (
              <div key={b.id} className="flex items-center gap-2 border-t border-dashed border-white/15 py-2 text-xs">
                <span className="min-w-0 flex-1 truncate">
                  <strong className="text-gold-bright">${(b.wager_cents / 100).toFixed(0)}</strong>
                  <span className="text-white/70"> · {b.terms}</span>
                </span>
                <span
                  className="shrink-0 text-[10px] font-bold tracking-wide"
                  style={{
                    color: b.status === 'won' ? '#10b981' : b.status === 'lost' ? '#ef4444' : b.status === 'pushed' ? 'rgba(255,255,255,0.5)' : '#dabb7a',
                  }}
                >
                  {b.status.toUpperCase()}
                </span>
              </div>
            ))}
            {allLiveBets.length > 3 && (
              <div className="pt-2 text-center text-[11px] text-white/50">+{allLiveBets.length - 3} more</div>
            )}
          </div>
        </div>
      )}

      {post.wager && !isRoundPost && (
        <button
          type="button"
          onClick={() => onOpenWager?.(post)}
          className="mx-4 mb-3 flex w-[calc(100%-2rem)] items-center gap-3 rounded-xl bg-navy p-3.5 text-left text-paper shadow-bt"
        >
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              post.wager.status === 'paid' ? 'bg-emerald-500 text-paper' : 'bg-gold text-navy'
            }`}
          >
            {post.wager.status === 'paid' ? <Check size={18} /> : <DollarSign size={18} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold tracking-wide text-gold-bright">
              {post.wager.status === 'paid' ? 'SETTLED' : 'WAGER PENDING'}
            </div>
            <div className="font-serif text-[15px] font-semibold text-paper">
              ${(post.wager.amount_cents / 100).toFixed(0)}
              {post.wager.note ? ` · ${post.wager.note}` : ''}
            </div>
          </div>
          <ChevronRight size={18} className="shrink-0 text-gold" />
        </button>
      )}

      <div className="mt-1 flex items-center gap-1 border-t border-line px-4 py-1">
        <button
          type="button"
          onClick={() => void toggleLike()}
          className={`flex items-center gap-1.5 px-2.5 py-2.5 text-[13px] font-semibold ${myLiked ? 'pop text-red-600' : 'text-ink-soft'}`}
        >
          <Heart size={20} fill={myLiked ? '#dc2626' : 'none'} color={myLiked ? '#dc2626' : '#475569'} strokeWidth={myLiked ? 0 : 2} />
          {likeCount > 0 && <span>{likeCount}</span>}
        </button>
        <button type="button" onClick={() => void toggleComments()} className="flex items-center gap-1.5 px-2.5 py-2.5 text-[13px] font-semibold text-ink-soft">
          <MessageCircle size={20} color="#475569" />
          {commentCount > 0 && <span>{commentCount}</span>}
        </button>
        <PostReactionPicker
          open={showReactionPicker}
          onOpenChange={setShowReactionPicker}
          myReactions={myReactions}
          onToggle={(emoji) => { void toggleReaction(emoji); }}
        />
        <div className="flex-1" />
        {canWager && (
          <button
            type="button"
            onClick={() => onOpenWager?.(post, { isNew: true })}
            className="flex items-center gap-1.5 px-2.5 py-2.5 text-[13px] font-semibold text-gold-deep"
          >
            <DollarSign size={18} color="#c4a662" />
            Wager
          </button>
        )}
      </div>

      {reactionEntries.length > 0 && (
        <div className="flex min-h-9 flex-wrap items-center gap-1.5 px-4 py-1">
          {reactionEntries.map(([emoji, count]) => (
            <button
              key={emoji}
              type="button"
              onClick={() => void toggleReaction(emoji)}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold text-ink-soft transition-colors duration-150 ${
                myReactions.includes(emoji) ? 'border-gold bg-gold/20' : 'border-line bg-cream'
              }`}
            >
              <span className="emoji text-sm">{emoji}</span>
              <span>{count}</span>
            </button>
          ))}
        </div>
      )}

      {reactionError && (
        <div className="px-4 pb-1 text-[11px] font-medium text-bad">{reactionError}</div>
      )}

      <div className="bg-cream/50 px-4 py-3">
        {!openComments && comments.length > 0 && (
          <div className="mb-3 space-y-2">
            {comments.slice(-2).map((c) => {
              const author = memberMap[c.author_id];
              return (
                <div key={c.id} className="flex items-start gap-2">
                  {author && <Avatar member={author} size={28} />}
                  <div className="min-w-0 flex-1 rounded-2xl bg-paper px-3 py-2 text-sm text-ink shadow-bt">
                    {author && <div className="mb-0.5 text-xs font-bold text-navy">{author.name}</div>}
                    {c.body}
                    <div className="mt-1 text-[10px] text-mute">{timeAgo(c.created_at)} ago</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {openComments && (
          <div className="mb-3 space-y-2">
            {loadingComments && <div className="text-sm text-mute">Loading…</div>}
            {comments.map((c) => {
              const author = memberMap[c.author_id];
              return (
                <div key={c.id} className="flex items-start gap-2">
                  {author && <Avatar member={author} size={28} />}
                  <div className="min-w-0 flex-1 rounded-2xl bg-paper px-3 py-2 text-sm text-ink shadow-bt">
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      {author && <span className="text-xs font-bold text-navy">{author.name}</span>}
                      {viewerId === c.author_id && (
                        <button
                          type="button"
                          onClick={() => setPendingDeleteCommentId(c.id)}
                          className="shrink-0 text-[10px] font-semibold text-red-600"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    {c.body}
                    <div className="mt-1 text-[10px] text-mute">{timeAgo(c.created_at)} ago</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {commentError && <div className="mb-2 text-xs text-red-600">{commentError}</div>}
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => { if (!openComments) setOpenComments(true); }}
            placeholder="Add a comment…"
            disabled={postingComment}
            className="flex-1 rounded-full border border-line bg-paper px-3 py-2 text-sm disabled:opacity-60"
          />
          <button
            type="button"
            disabled={postingComment || !draft.trim()}
            onClick={() => void addComment()}
            className="rounded-full bg-navy px-4 py-2 text-sm font-semibold text-cream disabled:opacity-50"
          >
            {postingComment ? '…' : 'Post'}
          </button>
        </div>
      </div>
    </article>
    {editing && (
      <EditPostSheet
        post={{ ...post, body: bodyText }}
        onClose={() => setEditing(false)}
        onSaved={(body) => {
          setBodyText(body);
          onPostUpdated?.(post.id, body);
        }}
      />
    )}
    <ConfirmDialog
      open={showDeletePostConfirm}
      title="Delete this post?"
      message="This cannot be undone."
      confirmLabel="Delete"
      destructive
      busy={deleting}
      onConfirm={() => void deletePost()}
      onCancel={() => setShowDeletePostConfirm(false)}
    />
    <ConfirmDialog
      open={pendingDeleteCommentId != null}
      title="Delete this comment?"
      confirmLabel="Delete"
      destructive
      onConfirm={() => pendingDeleteCommentId && void deleteComment(pendingDeleteCommentId)}
      onCancel={() => setPendingDeleteCommentId(null)}
    />
    </>
  );
}
